'use strict';

import { getWesogUserInfo, isUserLoggedIn } from './wesog-user';
import { checkValidAccessToken } from './oauth';
import { showNotification } from './notification-messages';
import { getCollection, getCollectionForNoAI } from './searcher';
import { cleanUrlParams, isDetailsView } from './utils';
import {
    ENDPOINT_UPLOAD_IMAGE,
    SEARCH_IMG_QUERY_PARAMS,
    ENDPOINT_SEARCH_RESULTS,
    ENDPOINT_GET_IMAGE,
    ENDPOINT_REPORTS_LIST,
    HOME_SLIDES_IMAGES,
    ENDPOINT_GET_REPORT_DETAILS,
    HOME_FIXED_WIDGETS,
    MOST_WANTED_URL,
    BAD_FILTERS_SEARCH_IMAGES_CODE,
    ENDPOINT_SEARCH_RESULTS_AI,
    BAD_IMAGE_ID_SEARCH_SIM_IMAGES_CODE,
    USER_NOT_FOUND_CODE,
    ENDPOINT_LIGHTBOX_LIST,
    ENDPOINT_DOWNLOAD_OPTIONS,
    PROV_FILTERS,
    ENDPOINT_CART_LIST,
    BASE_HOTS,
    ALLOWED_DATE_AI_FILTERS,
    SEARCH_RESULT_COLLECTION_KEY,
} from '@core/constants/API';
import {
    SEARCHER_TYPES,
    SEARCHER_OPTIONS,
    SEARCH_SIM_IMGS_SEPARATOR,
} from '@core/constants/SEARCHER';
import { NOTIFICATIONS_TYPE } from '@core/constants/NOTIFICATIONS';
import { REPORT_TYPE } from '@core/constants/REPORTS';
import {
    MAPPED_DATE_FILTERS,
    DATE_FILTERS,
    MAPPED_ORDER_BY,
    FORMAT_SEARCH_NO_AI_TYPES,
    MAPPED_GENERATIVE_AI_TYPES,
    MAPPED_PREMIUM_IMAGES_TYPES,
    MAPPED_VIDEO_ED_TYPE,
    SEARCHER_TYPE_FORMATS,
    MAPPED_SORT_BY,
    MAPPED_ORIENTATION_TYPES,
    MAPPED_TEMPLATE_TYPES,
    PREMIUM_IMAGES_TYPES,
    PERFORMANCE_FILTER_CATEGORIE_OPTIONS,
    MAPPED_NUM_PEOPLE_TYPES,
    IMGS_FACES_SEPARATOR,
} from '@core/constants/FILTERS';
import { delayTranslate, translate as t } from '@core/i18n/window-language';
import { removePrefixFilter } from './filter';
import { removeBaseCatType, getLastReportType } from './report';
import {
    formatFilterDate,
    getOnlyDateFromUTCDate,
    fixWrongDateFormats,
    isValidDate,
    getNDaysBeforeToday,
} from './date';
import { isLocalHost } from './utils';
import { ROUTES } from '@core/constants/ROUTES';
import {
    CURRENT_TAB,
    MAX_PAGE_FETCH_ALL_ELEMENTS,
    SUB_STRING_CODE,
} from '@core/constants/CONSTANTS';
import { getStoreConfiguration } from './store';
import { WESOG_STORE_CONFIGURATION_PARAMS } from '@core/constants/STORE';
import { ELEMENT_TYPE_ADD_LIGHTBOX } from '@core/constants/LIGHTBOX';

/** @type {AbortController} */
let controller = new AbortController();
/** @type {AbortSignal} */
let abortSignal = controller.signal;
/** @type {Promise<Response>} */
let currentFetch = null;

/**
 * Check if the report type is needed to send to the server is empty.
 * @param {URLSearchParams} urlParams - The url params.
 * @returns {boolean} - True if the report type is empty, false otherwise.
 * @private
 */
const _checkReportTypeToSend = (urlParams) => {
    return !urlParams.has(SEARCHER_TYPES.REPORT_TYPE);
};

/**
 * Get the type of the search. Images or videos.
 * @param {URLSearchParams} urlParams - The url params.
 * @param {object} params - The params to send.
 * @returns {object} - The params with the type of the search.
 * @private
 */
const _getTypeEndpointSearch = (urlParams, params) => {
    if (
        !urlParams.has(SEARCHER_TYPES.OPTION) ||
        urlParams.has(SEARCHER_TYPES.PLUGIN_AI)
    ) {
        params[SEARCH_IMG_QUERY_PARAMS.D_TYPE] = SEARCH_IMG_QUERY_PARAMS.IMAGES;

        return params;
    }

    /** @type {string} */
    const option = params[SEARCH_IMG_QUERY_PARAMS.FILTERS]?.length
        ? params[SEARCH_IMG_QUERY_PARAMS.FILTERS].find(
              (filter) =>
                  filter.includes(SEARCHER_OPTIONS.ED_CODE) ||
                  filter.includes(SEARCHER_OPTIONS.CR_CODE),
          )
        : '';

    if (option) {
        switch (true) {
            case option.includes(FORMAT_SEARCH_NO_AI_TYPES.IMG):
                params[SEARCH_IMG_QUERY_PARAMS.D_TYPE] = SEARCH_IMG_QUERY_PARAMS.IMAGES;
                break;
            case option.includes(FORMAT_SEARCH_NO_AI_TYPES.VID):
                params[SEARCH_IMG_QUERY_PARAMS.D_TYPE] = SEARCH_IMG_QUERY_PARAMS.VIDEOS;
                break;
            case option.includes(FORMAT_SEARCH_NO_AI_TYPES.ILL):
                params[SEARCH_IMG_QUERY_PARAMS.D_TYPE] = FORMAT_SEARCH_NO_AI_TYPES.ILL;
                break;
            case option.includes(FORMAT_SEARCH_NO_AI_TYPES.VEC):
                params[SEARCH_IMG_QUERY_PARAMS.D_TYPE] = FORMAT_SEARCH_NO_AI_TYPES.VEC;
                break;
            default:
                params[SEARCH_IMG_QUERY_PARAMS.D_TYPE] = params[
                    SEARCH_IMG_QUERY_PARAMS.AI
                ]
                    ? SEARCH_IMG_QUERY_PARAMS.IMAGES
                    : '';
                break;
        }
    }

    return params;
};

/**
 * Create the url query.
 * @type {function}
 * @param {object} params - The params to send.
 * @param {string} url - The url to fetch.
 * @returns {string} - The url with the query.
 * @private
 */
const _createURLQuery = (params, url) => {
    Object.keys(params).forEach((key) => {
        if (key === Object.keys(params)[0]) {
            url += url.includes('?') ? '&' : '?';
        }

        if (
            params[key] === '' ||
            (Array.isArray(params[key]) && params[key].length === 0)
        )
            return;

        if (Array.isArray(params[key])) {
            params[key].forEach((element) => {
                url += `${key}=${element}&`;
            });
        } else {
            url += `${key}=${params[key]}&`;
        }

        if (key === Object.keys(params)[Object.keys(params).length - 1]) {
            url = url.slice(0, -1);
        }
    });

    if (url.charAt(url.length - 1) === '&') {
        url = url.slice(0, -1);
    }

    return url;
};

/**
 * Abort the current fetch and start a new one.
 * @type {function}
 * @param {string} url - The url to fetch.
 * @param {object} options - The options to fetch.
 * @returns {Promise<Response>} - The fetch response.
 * @private
 */
const _abortAndFetch = (url, options) => {
    if (currentFetch) {
        controller.abort();
        controller = new AbortController();
        abortSignal = controller.signal;
    }

    currentFetch = fetch(url, { ...options, signal: abortSignal });

    return currentFetch;
};

/**
 * Check if the search images fields are correct.
 * @param {URLSearchParams} urlParams - The url params.
 * @returns {boolean} - True if the fields are correct, false otherwise.
 * @private
 */
const _checkSearchImagesFields = (urlParams) => {
    return (
        !urlParams.has(SEARCHER_TYPES.TEXT) &&
        !urlParams.has(SEARCHER_TYPES.IMAGE_ID) &&
        !urlParams.has(SEARCHER_TYPES.IMG_UID)
    );
};

/**
 * Get the report type for a section value.
 * @param {string} param - The param to get the report type.
 * @returns {string} - The report type for a section.
 * @private
 */
const _getReportTypeForSection = (param) => {
    if (!param.includes(SUB_STRING_CODE.CAT)) {
        return removeBaseCatType(REPORT_TYPE.ED_MOST_VIEWED);
    }

    return removeBaseCatType(removePrefixFilter(param));
};

/**
 * Adapt the params to the no AI server.
 * @param {object} params - The params to adapt.
 * @returns {object} - The adapted params.
 * @private
 */
const _adaptParamsToNoAIServer = (params) => {
    /* Adapt the page and limit items per page */
    ++params[SEARCH_IMG_QUERY_PARAMS.PAGE_AI];
    params[SEARCH_IMG_QUERY_PARAMS.LIMIT] = params[SEARCH_IMG_QUERY_PARAMS.LIMIT_AI];

    if (params[SEARCH_IMG_QUERY_PARAMS.DATE_AI]) {
        params[SEARCH_IMG_QUERY_PARAMS.DATE] =
            MAPPED_DATE_FILTERS[params[SEARCH_IMG_QUERY_PARAMS.DATE_AI]] ||
            MAPPED_DATE_FILTERS.dat;

        if (params[SEARCH_IMG_QUERY_PARAMS.DATE] === MAPPED_DATE_FILTERS.dat) {
            params[SEARCH_IMG_QUERY_PARAMS.DATE] =
                params[SUB_STRING_CODE.DAT_FROM.replace('dat', 'date').toLowerCase()];
        }

        if (params[SUB_STRING_CODE.DAT_TO.replace('dat', 'date').toLowerCase()]) {
            params[SEARCH_IMG_QUERY_PARAMS.DATE_TO] =
                params[SUB_STRING_CODE.DAT_TO.replace('dat', 'date').toLowerCase()];
        }
    }

    if (params[SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI]) {
        params[SEARCH_IMG_QUERY_PARAMS.SORT_BY] =
            MAPPED_ORDER_BY[params[SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI]];
    }

    /* Remove unnecessary params */
    delete params[SEARCH_IMG_QUERY_PARAMS.LIMIT_AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.DATE_AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.FACES];
    delete params[SEARCH_IMG_QUERY_PARAMS.FACES_NEG];
    delete params[SEARCH_IMG_QUERY_PARAMS.IMAGES];
    delete params[SEARCH_IMG_QUERY_PARAMS.IMAGES_NEG];
    delete params[SUB_STRING_CODE.DAT_FROM.replace('dat', 'date').toLowerCase()];
    delete params[SUB_STRING_CODE.DAT_TO.replace('dat', 'date').toLowerCase()];

    return params;
};

/**
 * Adapt the params to the AI server.
 * @param {object} params - The params to adapt.
 * @param {URLSearchParams} urlParams - The url params.
 * @returns {object} - The adapted params.
 * @private
 */
const _adaptParamsToAIServer = (params, urlParams) => {
    /* Fields that I need */
    params['count'] = true;
    ++params[SEARCH_IMG_QUERY_PARAMS.PAGE_AI];

    if (params[SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI]) {
        const [sortBy, sortOrder] =
            MAPPED_SORT_BY[params[SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI]];
        params['sort_by'] = sortBy;
        params['sort_order'] = sortOrder;
    }

    /** @type {function} - Add the '&&' separator if we have no one*/
    const _addAndOperator = (_q) => {
        return _q ? _q + '&&' : _q;
    };

    let q = '';
    let hasText = false;

    if (params[SEARCH_IMG_QUERY_PARAMS.TEXT]) {
        q = _addAndOperator(q);
        q = q + `t~${params[SEARCH_IMG_QUERY_PARAMS.TEXT]}`;
        hasText = true;
    }

    if (params[SEARCH_IMG_QUERY_PARAMS.IMAGES]?.length) {
        q = _addAndOperator(q);
        const provider = params[SEARCH_IMG_QUERY_PARAMS.PROV];
        const providerFilter =
            provider && provider !== PROV_FILTERS.GTRES
                ? provider + SEARCH_SIM_IMGS_SEPARATOR
                : '';

        q =
            q +
            `image${hasText ? '~~' : '~~~'}${providerFilter}${params[SEARCH_IMG_QUERY_PARAMS.IMAGES]}`;
    }

    /* NOTE: keep commenting until add the negative logic */
    // if (params[SEARCH_IMG_QUERY_PARAMS.IMAGES_NEG]?.length) {
    //     q = _addAndOperator(q);
    //     q = q + `!image${hasText ? '~~' : '~~~'}${providerFilter}${params[SEARCH_IMG_QUERY_PARAMS.IMAGES_NEG]}`;
    // }

    if (getCollection(urlParams) !== SEARCH_IMG_QUERY_PARAMS.CREATIVE) {
        if (
            params[SEARCH_IMG_QUERY_PARAMS.FACES] &&
            params[SEARCH_IMG_QUERY_PARAMS.FACES].length
        ) {
            params[SEARCH_IMG_QUERY_PARAMS.FACES].forEach((face) => {
                q = _addAndOperator(q);
                q = q + `person~${face.replace(IMGS_FACES_SEPARATOR, '_')}`;
            });
        }

        if (
            params[SEARCH_IMG_QUERY_PARAMS.FACES_NEG] &&
            params[SEARCH_IMG_QUERY_PARAMS.FACES_NEG].length
        ) {
            params[SEARCH_IMG_QUERY_PARAMS.FACES_NEG].forEach((face) => {
                q = _addAndOperator(q);
                q = q + `!person~${face.replace(IMGS_FACES_SEPARATOR, '_')}`;
            });
        }
    }

    if (
        params[SUB_STRING_CODE.DAT] &&
        ALLOWED_DATE_AI_FILTERS.includes(params[SUB_STRING_CODE.DAT])
    ) {
        const /** @type {string} */ today = new Date().toISOString().split('T')[0];
        let /** @type {Date | undefined} */ dateTo;
        switch (true) {
            case params[SUB_STRING_CODE.DAT] === DATE_FILTERS.LAST_24_HOURS: {
                q = _addAndOperator(q);

                dateTo = getNDaysBeforeToday(1);
                break;
            }
            case params[SUB_STRING_CODE.DAT] === DATE_FILTERS.LAST_7_DAYS: {
                q = _addAndOperator(q);

                dateTo = getNDaysBeforeToday(7);
                break;
            }
            case params[SUB_STRING_CODE.DAT] === DATE_FILTERS.LAST_30_DAYS: {
                q = _addAndOperator(q);

                dateTo = getNDaysBeforeToday(30);
                break;
            }
            case params[SUB_STRING_CODE.DAT] === DATE_FILTERS.LAST_12_MONTHS: {
                q = _addAndOperator(q);

                dateTo = getNDaysBeforeToday(365);
                break;
            }
        }
        q = q + `date<=${today}&&date>=${getOnlyDateFromUTCDate(dateTo)}`;
    }

    const /** @type {string} */ dateFromKey = SUB_STRING_CODE.DAT_FROM.replace(
            'dat',
            'date',
        ).toLowerCase();
    const /** @type {string} */ dateToKey = SUB_STRING_CODE.DAT_TO.replace(
            'dat',
            'date',
        ).toLowerCase();

    if (params[dateFromKey]) {
        let /** @type {string} */ dateTo = params[dateToKey]
                ? getOnlyDateFromUTCDate(new Date(fixWrongDateFormats(params[dateToKey])))
                : getOnlyDateFromUTCDate(new Date());

        q = _addAndOperator(q);
        q =
            q +
            `date<=${dateTo}&&date>=${getOnlyDateFromUTCDate(
                new Date(fixWrongDateFormats(params[dateFromKey])),
            )}`;
    }

    if (params[SEARCH_IMG_QUERY_PARAMS.CATEGORIE]?.length) {
        q = _addAndOperator(q);
        q =
            q +
            params[SEARCH_IMG_QUERY_PARAMS.CATEGORIE]
                .map((categorie) => {
                    if (categorie.toUpperCase() === 'EVENTOS') {
                        return `(k=ACTOS OFICIALES||k=ACTOS PUBLICOS)`;
                    }

                    if (categorie.toUpperCase() === 'ESPECTACULOS') {
                        return (
                            '(' +
                            PERFORMANCE_FILTER_CATEGORIE_OPTIONS.map(
                                (keyword) => `k=${keyword}`,
                            ).join('||') +
                            ')'
                        );
                    }

                    if (categorie.toUpperCase() === 'PAPARAZZI') {
                        return 'k=PAPARAZZI';
                    }

                    return `categories=${categorie}`;
                })
                .join('&&');
    }

    if (params[SEARCH_IMG_QUERY_PARAMS.ORIENTATION]) {
        q = _addAndOperator(q);
        q =
            q +
            `o=${MAPPED_ORIENTATION_TYPES[params[SEARCH_IMG_QUERY_PARAMS.ORIENTATION]]}`;
    }

    if (params[SEARCH_IMG_QUERY_PARAMS.PEOPLE] !== undefined) {
        q = _addAndOperator(q);
        if (params[SEARCH_IMG_QUERY_PARAMS.PEOPLE] === 0) {
            q = q + `n_people=${params[SEARCH_IMG_QUERY_PARAMS.PEOPLE]}`;
        } else {
            q =
                q +
                `k=${MAPPED_NUM_PEOPLE_TYPES[
                    params[SEARCH_IMG_QUERY_PARAMS.PEOPLE]
                ].toUpperCase()}`;
        }
    }

    if (params[SEARCH_IMG_QUERY_PARAMS.TEMPLATE] !== undefined) {
        q = _addAndOperator(q);
        q =
            q +
            `k=${MAPPED_TEMPLATE_TYPES[
                params[SEARCH_IMG_QUERY_PARAMS.TEMPLATE]
            ].toUpperCase()}`;
    }

    if (params[SEARCH_IMG_QUERY_PARAMS.GENERATIVE_AI_AI]) {
        q = _addAndOperator(q);
        q =
            q +
            `ai=${!MAPPED_GENERATIVE_AI_TYPES[
                params[SEARCH_IMG_QUERY_PARAMS.GENERATIVE_AI_AI]
            ]}`;
    }

    if (params[SEARCH_IMG_QUERY_PARAMS.PREMIUM_IMAGES]) {
        q = _addAndOperator(q);
        q =
            q +
            `${
                params[SEARCH_IMG_QUERY_PARAMS.PREMIUM_IMAGES] ===
                PREMIUM_IMAGES_TYPES.NON_ONLY
                    ? '!'
                    : ''
            }k=IMAGENES PREMIUM`;
    }

    params['q'] = q;

    /* Remove unnecessary params */
    delete params[SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.TEXT];
    delete params[SEARCH_IMG_QUERY_PARAMS.D_TYPE];
    delete params[SEARCH_IMG_QUERY_PARAMS.IMAGES];
    delete params[SEARCH_IMG_QUERY_PARAMS.IMAGES_NEG];
    delete params[SEARCH_IMG_QUERY_PARAMS.FACES];
    delete params[SEARCH_IMG_QUERY_PARAMS.FACES_NEG];
    delete params[SEARCH_IMG_QUERY_PARAMS.FILTERS];
    delete params[SUB_STRING_CODE.DAT];
    delete params[dateFromKey];
    delete params[dateToKey];
    delete params[SEARCH_IMG_QUERY_PARAMS.CATEGORIE];
    delete params[SEARCH_IMG_QUERY_PARAMS.ORIENTATION];
    delete params[SEARCH_IMG_QUERY_PARAMS.PEOPLE];
    delete params[SEARCH_IMG_QUERY_PARAMS.TEMPLATE];
    delete params[SEARCH_IMG_QUERY_PARAMS.GENERATIVE_AI_AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.PREMIUM_IMAGES];

    return params;
};

/**
 * Prepare the params to send to the server.
 * @param {object} params - The params to prepare.
 * @returns {object} - The prepared params.
 * @private
 */
const _prepareParamFilters = (params) => {
    if (params[SEARCH_IMG_QUERY_PARAMS.FILTERS].length === 0) {
        return params;
    }

    /** @type {object} */
    let transformedParams = {
        [SEARCH_IMG_QUERY_PARAMS.PAGE_AI]: params[SEARCH_IMG_QUERY_PARAMS.PAGE_AI],
        [SEARCH_IMG_QUERY_PARAMS.LIMIT_AI]: params[SEARCH_IMG_QUERY_PARAMS.LIMIT_AI],
        [SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI]: params[SEARCH_IMG_QUERY_PARAMS.SORT_BY_AI],
        [SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY]:
            params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY],
        ...(params[SEARCH_IMG_QUERY_PARAMS.TEXT] && {
            [SEARCH_IMG_QUERY_PARAMS.TEXT]: params[SEARCH_IMG_QUERY_PARAMS.TEXT],
        }),
        ...(params[SEARCH_IMG_QUERY_PARAMS.D_TYPE] && {
            [SEARCH_IMG_QUERY_PARAMS.D_TYPE]: params[SEARCH_IMG_QUERY_PARAMS.D_TYPE],
        }),
        ...(params[SEARCH_IMG_QUERY_PARAMS.KEYWORDS] && {
            [SEARCH_IMG_QUERY_PARAMS.KEYWORDS]: params[SEARCH_IMG_QUERY_PARAMS.KEYWORDS],
        }),
        ...(params[SEARCH_IMG_QUERY_PARAMS.RANGE] && {
            range: params[SEARCH_IMG_QUERY_PARAMS.RANGE],
        }),
    };

    if (params[SEARCH_IMG_QUERY_PARAMS.AI]) {
        transformedParams = {
            ...transformedParams,
            [SEARCH_IMG_QUERY_PARAMS.AI]: params[SEARCH_IMG_QUERY_PARAMS.AI],
            [SEARCH_IMG_QUERY_PARAMS.RECURSIVE_AI_SEARCH]:
                params[SEARCH_IMG_QUERY_PARAMS.RECURSIVE_AI_SEARCH],
            ...(params[SEARCH_IMG_QUERY_PARAMS.CURRENT_TAB] && {
                [SEARCH_IMG_QUERY_PARAMS.CURRENT_TAB]:
                    params[SEARCH_IMG_QUERY_PARAMS.CURRENT_TAB],
            }),
            ...(params[SEARCH_IMG_QUERY_PARAMS.FACES] && {
                [SEARCH_IMG_QUERY_PARAMS.FACES]: params[SEARCH_IMG_QUERY_PARAMS.FACES],
            }),
            ...(params[SEARCH_IMG_QUERY_PARAMS.FACES_NEG] && {
                [SEARCH_IMG_QUERY_PARAMS.FACES_NEG]:
                    params[SEARCH_IMG_QUERY_PARAMS.FACES_NEG],
            }),
            ...(params[SEARCH_IMG_QUERY_PARAMS.IMAGES] && {
                [SEARCH_IMG_QUERY_PARAMS.IMAGES]: params[SEARCH_IMG_QUERY_PARAMS.IMAGES],
            }),
            ...(params[SEARCH_IMG_QUERY_PARAMS.IMAGES_NEG] && {
                [SEARCH_IMG_QUERY_PARAMS.IMAGES_NEG]:
                    params[SEARCH_IMG_QUERY_PARAMS.IMAGES_NEG],
            }),
        };
    }

    params[SEARCH_IMG_QUERY_PARAMS.FILTERS].forEach((filter) => {
        const [key, value] = filter.split(':');

        /* Report categories */
        if (key.includes(SUB_STRING_CODE.CAT_REP)) {
            return;
        }

        /* Dates */
        if (key.includes(SUB_STRING_CODE.DAT)) {
            switch (true) {
                case key.includes(DATE_FILTERS.CUSTOM_DATE_FROM):
                    transformedParams[
                        SUB_STRING_CODE.DAT_FROM.replace('dat', 'date').toLowerCase()
                    ] = formatFilterDate(value);
                    break;
                case key.includes(DATE_FILTERS.CUSTOM_DATE_TO):
                    transformedParams[
                        SUB_STRING_CODE.DAT_TO.replace('dat', 'date').toLowerCase()
                    ] = formatFilterDate(value);
                    break;
                default:
                    transformedParams[SUB_STRING_CODE.DAT] = value;
                    break;
            }

            transformedParams[SUB_STRING_CODE.DAT] = value;
            return;
        }

        /* Search result categories without AI */
        if (
            key.includes(SUB_STRING_CODE.CAT_ED) ||
            key.includes(SUB_STRING_CODE.CAT_CR)
        ) {
            transformedParams[SEARCH_IMG_QUERY_PARAMS.CATEGORIE] = [
                ...(transformedParams[SEARCH_IMG_QUERY_PARAMS.CATEGORIE] || []),
                value
                    .replace(`${SUB_STRING_CODE.CAT_ED}`, '')
                    .replace(`${SUB_STRING_CODE.CAT_CR}`, ''),
            ];
            return;
        }

        /* Keywords without AI */
        if (
            !params[SEARCH_IMG_QUERY_PARAMS.AI] &&
            key.includes(SUB_STRING_CODE.KEYWORDS)
        ) {
            transformedParams[SEARCH_IMG_QUERY_PARAMS.KEYWORDS] = [
                ...(transformedParams[SEARCH_IMG_QUERY_PARAMS.KEYWORDS] || []),
                value
                    .replace(`${SUB_STRING_CODE.KEYWORDS}`, '')
                    .replace(`${SUB_STRING_CODE.KEYWORDS}`, ''),
            ];
            return;
        }

        /* Search result composition */
        if (
            key.includes(SUB_STRING_CODE.COM_ED) ||
            key.includes(SUB_STRING_CODE.COM_CR)
        ) {
            if (value.includes(SUB_STRING_CODE.COM_ORI)) {
                switch (true) {
                    case value.includes(SUB_STRING_CODE.COM_ORI_VER):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.ORIENTATION] = 1;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_ORI_HOR):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.ORIENTATION] = 2;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_ORI_SQU):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.ORIENTATION] = 3;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_ORI_PAN):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.ORIENTATION] = 4;
                        break;
                    default:
                        break;
                }

                return;
            }

            if (value.includes(SUB_STRING_CODE.COM_PEO)) {
                switch (true) {
                    case value.includes(SUB_STRING_CODE.COM_PEO_WITHOUT):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.PEOPLE] = 0;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_PEO_IND):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.PEOPLE] = 1;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_PEO_PART):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.PEOPLE] = 2;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_PEO_GROUP):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.PEOPLE] = 3;
                        break;
                    default:
                        break;
                }

                return;
            }

            if (value.includes(SUB_STRING_CODE.COM_FRAM)) {
                switch (true) {
                    case value.includes(SUB_STRING_CODE.COM_FRAM_FIRST):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.TEMPLATE] = 0;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_FRAM_MIDDLE):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.TEMPLATE] = 1;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_FRAM_FULL):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.TEMPLATE] = 2;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_FRAM_GEN):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.TEMPLATE] = 3;
                        break;
                    case value.includes(SUB_STRING_CODE.COM_FRAM_AIR):
                        transformedParams[SEARCH_IMG_QUERY_PARAMS.TEMPLATE] = 4;
                        break;
                    default:
                        break;
                }

                return;
            }

            return;
        }

        /* Generative AI */
        if (key.includes(SUB_STRING_CODE.GENE_CR)) {
            transformedParams[SEARCH_IMG_QUERY_PARAMS.GENERATIVE_AI_AI] = value;
            return;
        }

        /* Premium images */
        if (key.includes(SUB_STRING_CODE.PREMIUM_CR)) {
            transformedParams[SEARCH_IMG_QUERY_PARAMS.PREMIUM_IMAGES] = value;
            return;
        }

        /* Video type filter */
        if (key.includes(SUB_STRING_CODE.VID_ED_TYPE)) {
            transformedParams[SEARCH_IMG_QUERY_PARAMS.KEYWORDS] = [
                ...(transformedParams[SEARCH_IMG_QUERY_PARAMS.KEYWORDS] || []),
                MAPPED_VIDEO_ED_TYPE[
                    value.replace(SUB_STRING_CODE.VID_ED_TYPE, '').toUpperCase()
                ],
            ];
            return;
        }

        /* People name */
        if (
            key.includes(SUB_STRING_CODE.PEO) &&
            value.includes(SUB_STRING_CODE.PEO_NAME)
        ) {
            transformedParams[SEARCH_IMG_QUERY_PARAMS.KEYWORDS] = [
                ...(transformedParams[SEARCH_IMG_QUERY_PARAMS.KEYWORDS] || []),
                value.replace(SUB_STRING_CODE.PEO_NAME, ''),
            ];
            return;
        }
    });

    return transformedParams;
};

/**
 * Format the today date.
 * @returns {string} - The formatted today date.
 * @private
 */
const _formattedTodayDate = () => {
    const /** @type {Date} */ today = new Date();
    return `${today.getDate()}/${today.getMonth() + 1}/${today.getFullYear()}`;
};

/**
 * Conditional to  load the previous actives if the page is 3 or less.
 * @param {Boolean} firstQuery - The first query.
 * @param {object} params - The params to search.
 * @returns {object} - The params to search.
 * @deprecated
 * @private
 */
// eslint-disable-next-line no-unused-vars
const _handleFirstQueryPageNoScrollPagination = (firstQuery, params) => {
    const /** @type {Boolean} */ paginationConfig =
            getStoreConfiguration()[WESOG_STORE_CONFIGURATION_PARAMS.PAGINATION];

    if (
        paginationConfig &&
        firstQuery &&
        params[SEARCH_IMG_QUERY_PARAMS.PAGE_AI] &&
        params[SEARCH_IMG_QUERY_PARAMS.PAGE_AI] <= MAX_PAGE_FETCH_ALL_ELEMENTS
    ) {
        return {
            ...params,
            [SEARCH_IMG_QUERY_PARAMS.PAGE_AI]: 0,
            [SEARCH_IMG_QUERY_PARAMS.LIMIT_AI]:
                params[SEARCH_IMG_QUERY_PARAMS.PAGE_AI] *
                params[SEARCH_IMG_QUERY_PARAMS.LIMIT_AI],
        };
    }

    return params;
};

/**
 * Avoid searching images with text but with a disabled image or character.
 * @param {object} params - The params to search.
 * @param {URLSearchParams} urlParams - The url params.
 * @returns {boolean} - True if the search is not allowed, false otherwise.
 */
// eslint-disable-next-line no-unused-vars
const _searchImagesWithText = (params, urlParams) => {
    params[SEARCH_IMG_QUERY_PARAMS.AI] &&
        urlParams.has(SEARCHER_TYPES.OPTION) &&
        urlParams.get(SEARCHER_TYPES.OPTION).includes(SEARCHER_OPTIONS.ED_CODE) &&
        !params[SEARCH_IMG_QUERY_PARAMS.IMAGES].length &&
        !params[SEARCH_IMG_QUERY_PARAMS.FACES].length &&
        params[SEARCH_IMG_QUERY_PARAMS.TEXT].length;
};

/**
 * Build the headers for the request.
 * @returns {{
 *  token: string,
 *  'Content-Type': string,
 * }} - The headers for the request.
 * @private
 */
const _buildHeaders = () => {
    /** @type {{ username: string, access_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string, lightbox: string}} */
    const user = getWesogUserInfo();
    return {
        token: `${user.access_token}`,
        'Content-Type': 'application/json',
    };
};

/**
 * Switch the format option.
 * @param {string} option - The option to switch.
 * @returns {number} - The format option.
 * @private
 */
const _switchFormatOption = (option) => {
    /** @type {number} */
    let format = 0;

    switch (true) {
        case option.toLowerCase().includes(FORMAT_SEARCH_NO_AI_TYPES.VID.toLowerCase()):
            format = SEARCHER_TYPE_FORMATS[FORMAT_SEARCH_NO_AI_TYPES.VID];
            break;
        case option.toLowerCase().includes(FORMAT_SEARCH_NO_AI_TYPES.ILL.toLowerCase()):
            format = SEARCHER_TYPE_FORMATS[FORMAT_SEARCH_NO_AI_TYPES.ILL];
            break;
        case option.toLowerCase().includes(FORMAT_SEARCH_NO_AI_TYPES.VEC.toLowerCase()):
            format = SEARCHER_TYPE_FORMATS[FORMAT_SEARCH_NO_AI_TYPES.VEC];
            break;
        default:
            format = SEARCHER_TYPE_FORMATS[FORMAT_SEARCH_NO_AI_TYPES.IMG];
            break;
    }

    return format;
};

/**
 * Get the dynamic widgets from the server.
 * @type {function}
 * @param {object} params - The params to search.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const getDynamicWidgets = async (params) => {
    /** @type {string} */
    let url = `${ENDPOINT_REPORTS_LIST}/${params[SEARCH_IMG_QUERY_PARAMS.SECTION]}`;
    /* Remove section param from the params */
    delete params[SEARCH_IMG_QUERY_PARAMS.SECTION];
    url = _createURLQuery(params, url);

    /** @type {Response} */
    const response = await fetch(url, {
        method: 'GET',
        headers: _buildHeaders(),
    });

    return response;
};

/**
 * Upload an image to the server and return the response.
 * @type {function}
 * @param {File} image - The image to upload.
 * @returns {Promise<Response>} - The response from the server in json format.
 */
export const uploadImage = async (image) => {
    /** @type {FormData} */
    const formData = new FormData();
    formData.append('image', image);

    /** @type {{ username: string, access_token: string, refresh_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string, lightbox: string}} */
    const user = getWesogUserInfo();

    await checkValidAccessToken();

    const /** @type {String} */ url = `${ENDPOINT_UPLOAD_IMAGE}/${getCollectionForNoAI(
            new URLSearchParams(window.location.search),
        )}`;

    /** @type {Response} */
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            token: `${user.access_token}`,
        },
        body: formData,
    });

    return response;
};

/**
 * Search images in the server.
 * @param {object} params - The params to search.
 * @returns {Promise<Response>} - The response from the server in json format
 * @throws {Error} - If the response is not ok
 */
export const searchImage = async (params) => {
    if (params[SEARCH_IMG_QUERY_PARAMS.AI_SERVER_MAINTENANCE]) {
        throw new Error('AI server is under maintenance');
    }

    params[SEARCH_IMG_QUERY_PARAMS.TEXT] = params[SEARCH_IMG_QUERY_PARAMS.TEXT].trim();

    /** @type {URLSearchParams} */
    let urlParams = new URLSearchParams(window.location.search);
    urlParams = cleanUrlParams(urlParams);

    let /** @type {Function} */ fetchMethod = _abortAndFetch;

    if (
        params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY] ||
        params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS]
    )
        fetchMethod = fetch;

    /** @type {function} */
    const _checkMinFilters = (params) => {
        return (
            !params[SEARCH_IMG_QUERY_PARAMS.IMAGES].length &&
            !params[SEARCH_IMG_QUERY_PARAMS.TEXT].length &&
            !params[SEARCH_IMG_QUERY_PARAMS.FACES].length
        );
    };

    if (
        params[SEARCH_IMG_QUERY_PARAMS.AI] &&
        (_checkSearchImagesFields(urlParams) || _checkMinFilters(params))
    ) {
        throw new Error(
            `${BAD_FILTERS_SEARCH_IMAGES_CODE} - Bad filters to search images`,
        );
    }

    params = _getTypeEndpointSearch(urlParams, params);
    params = _prepareParamFilters(params);

    const format = _switchFormatOption(params[SEARCH_IMG_QUERY_PARAMS.D_TYPE]);

    params = _adaptParamsToAIServer(params, urlParams);

    delete params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY];
    delete params[SEARCH_IMG_QUERY_PARAMS.AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.PAGE_REPORT_AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.RANGE];
    delete params[SEARCH_IMG_QUERY_PARAMS.CURRENT_TAB];
    delete params[SEARCH_IMG_QUERY_PARAMS.RECURSIVE_AI_SEARCH];
    delete params[SEARCH_IMG_QUERY_PARAMS.AI_SERVER_MAINTENANCE];
    delete params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS];
    delete params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS_PAGE];
    delete params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS_LAST_PAGE];

    const /** @type {object} */ body = {
            ...(urlParams.has(SEARCHER_TYPES.OPTION) && {
                collection: getCollectionForNoAI(urlParams),
            }),
            parameters: new URLSearchParams(params).toString(),
            format,
        };

    const /** @type {string} */ url = ENDPOINT_SEARCH_RESULTS_AI;

    /** @type {Response} */
    const response = await fetchMethod(url, {
        method: 'POST',
        headers: _buildHeaders(),
        body: JSON.stringify(body),
    });

    return response;
};

/**
 * Search images in the server.
 * @type {function}
 * @param {object} params - The params to search.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const searchImageNoAI = async (params) => {
    let /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );
    urlParams = cleanUrlParams(urlParams);

    if (!window.location.pathname.includes(ROUTES.HOME)) {
        params[SEARCH_IMG_QUERY_PARAMS.TEXT] =
            params[SEARCH_IMG_QUERY_PARAMS.TEXT].trim();

        if (
            _checkSearchImagesFields(urlParams) &&
            !urlParams.has(SEARCHER_TYPES.KEYWORDS) &&
            !urlParams.has(SEARCHER_TYPES.REPORT_TYPE)
        ) {
            throw new Error(
                `${BAD_FILTERS_SEARCH_IMAGES_CODE} - Bad filters to search images`,
            );
        }
    }

    /** @type {function} */
    let fetchMethod = _abortAndFetch;

    if (
        params[SEARCH_IMG_QUERY_PARAMS.CURRENT_TAB] === CURRENT_TAB.REPORTS ||
        params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY] ||
        params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS]
    ) {
        fetchMethod = fetch;
    }

    params = _getTypeEndpointSearch(new URLSearchParams(window.location.search), params);
    params = _prepareParamFilters(params);
    params = _adaptParamsToNoAIServer(params);

    /** @type {function} */
    const _prepareSearchImageNoAIParams = (params) => {
        return [
            {
                [SEARCH_IMG_QUERY_PARAMS.SORT_BY]:
                    params[SEARCH_IMG_QUERY_PARAMS.SORT_BY],
                [SEARCH_IMG_QUERY_PARAMS.PAGE_AI]:
                    params[SEARCH_IMG_QUERY_PARAMS.PAGE_AI],
                [SEARCH_IMG_QUERY_PARAMS.LIMIT]: params[SEARCH_IMG_QUERY_PARAMS.LIMIT],
            },
            {
                ...(params[SEARCH_IMG_QUERY_PARAMS.TEXT] && {
                    query: params[SEARCH_IMG_QUERY_PARAMS.TEXT],
                }),
                ...(params[SEARCH_IMG_QUERY_PARAMS.DATE] && {
                    [SEARCH_IMG_QUERY_PARAMS.DATE]: params[SEARCH_IMG_QUERY_PARAMS.DATE],
                    ...(isValidDate(params[SEARCH_IMG_QUERY_PARAMS.DATE]) && {
                        [SEARCH_IMG_QUERY_PARAMS.DATE_TO]: _formattedTodayDate(),
                    }),
                }),
                ...(params[SEARCH_IMG_QUERY_PARAMS.DATE_TO] &&
                    params[SEARCH_IMG_QUERY_PARAMS.DATE] && {
                        [SEARCH_IMG_QUERY_PARAMS.DATE_TO]:
                            params[SEARCH_IMG_QUERY_PARAMS.DATE_TO],
                    }),
                ...(params[SEARCH_IMG_QUERY_PARAMS.D_TYPE] &&
                    urlParams.has(SEARCHER_TYPES.OPTION) &&
                    !urlParams.get(SEARCHER_TYPES.OPTION).includes('All') && {
                        format: _switchFormatOption(
                            params[SEARCH_IMG_QUERY_PARAMS.D_TYPE],
                        ),
                    }),
                ...(params[SEARCH_IMG_QUERY_PARAMS.CATEGORIE] && {
                    categorie: params[SEARCH_IMG_QUERY_PARAMS.CATEGORIE],
                }),
                ...(urlParams.has(SEARCHER_TYPES.OPTION) && {
                    collection: getCollectionForNoAI(urlParams),
                }),
                ...(params[SEARCH_IMG_QUERY_PARAMS.ORIENTATION] && {
                    orientation: params[SEARCH_IMG_QUERY_PARAMS.ORIENTATION],
                }),
                ...(!isNaN(params[SEARCH_IMG_QUERY_PARAMS.PEOPLE]) && {
                    people: params[SEARCH_IMG_QUERY_PARAMS.PEOPLE],
                }),
                ...(!isNaN(params[SEARCH_IMG_QUERY_PARAMS.TEMPLATE]) && {
                    template: params[SEARCH_IMG_QUERY_PARAMS.TEMPLATE],
                }),
                ...(params[SEARCH_IMG_QUERY_PARAMS.GENERATIVE_AI_AI] && {
                    ai: MAPPED_GENERATIVE_AI_TYPES[
                        params[SEARCH_IMG_QUERY_PARAMS.GENERATIVE_AI_AI]
                    ],
                }),
                ...(params[SEARCH_IMG_QUERY_PARAMS.PREMIUM_IMAGES] && {
                    premium:
                        MAPPED_PREMIUM_IMAGES_TYPES[
                            params[SEARCH_IMG_QUERY_PARAMS.PREMIUM_IMAGES]
                        ],
                }),
                ...(params[SEARCH_IMG_QUERY_PARAMS.KEYWORDS] && {
                    keywords: params[SEARCH_IMG_QUERY_PARAMS.KEYWORDS],
                }),
                ...(params[SEARCH_IMG_QUERY_PARAMS.RANGE] && {
                    range: params[SEARCH_IMG_QUERY_PARAMS.RANGE],
                }),
            },
        ];
    };

    let [finalParams, finalBody] = _prepareSearchImageNoAIParams(params);
    /** @type {string} */
    let url = ENDPOINT_SEARCH_RESULTS;

    url = _createURLQuery(finalParams, url);

    /** @type {Response} */
    const response = await fetchMethod(url, {
        method: 'POST',
        headers: _buildHeaders(),
        body: JSON.stringify(finalBody),
    });

    return response;
};

/**
 * Get an image from the server.
 * @param {string} img_uid - The image uid.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const getImage = async (img_uid) => {
    /** @type {Response} */
    const response = await fetch(`${ENDPOINT_GET_IMAGE}?id=${img_uid}`, {
        method: 'GET',
        headers: _buildHeaders(),
    });

    return response;
};

/**
 * Search reports in the server.
 * @param {object} params - The params to search.
 * @returns {Promise<Response>} - The response from the server in json format
 * @throws {Error} - If the response is not ok
 */
export const getReportList = async (params) => {
    /** @type {URLSearchParams} */
    let urlParams = new URLSearchParams(window.location.search);
    urlParams = cleanUrlParams(urlParams);

    if (_checkReportTypeToSend(urlParams)) {
        throw new Error('Error searching the report');
    }

    let /** @type {Function} */ fetchMethod = _abortAndFetch;

    if (
        params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY] ||
        params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS]
    )
        fetchMethod = fetch;

    /** @type {string} */
    const lastReportType = getLastReportType(params[SEARCH_IMG_QUERY_PARAMS.FILTERS]);

    /** @type {string} */
    const currentSearchOption =
        urlParams.get(SEARCHER_TYPES.OPTION) || SEARCHER_OPTIONS.ED_IMGS;

    /** @type {string} */
    let url = lastReportType
        ? `${ENDPOINT_REPORTS_LIST}/${_getReportTypeForSection(lastReportType)}`
        : `${HOME_SLIDES_IMAGES}?${
              SEARCH_IMG_QUERY_PARAMS.EDITORIAL
          }=${currentSearchOption.includes(SUB_STRING_CODE.ED)}`;

    params = _prepareParamFilters(params);
    params = _adaptParamsToNoAIServer(params);

    /** @type {function} */
    const _prepareSearchReportNoAIParams = (params) => {
        if (
            params[SEARCH_IMG_QUERY_PARAMS.DATE_TO] &&
            params[SEARCH_IMG_QUERY_PARAMS.DATE]
        ) {
            params[SEARCH_IMG_QUERY_PARAMS.DATE] = `${
                params[SEARCH_IMG_QUERY_PARAMS.DATE]
            }|${params[SEARCH_IMG_QUERY_PARAMS.DATE_TO]}`;
        } else if (
            params[SEARCH_IMG_QUERY_PARAMS.DATE] &&
            isValidDate(params[SEARCH_IMG_QUERY_PARAMS.DATE])
        ) {
            const /** @type {string} */ formattedDate = _formattedTodayDate();

            if (formattedDate !== params[SEARCH_IMG_QUERY_PARAMS.DATE]) {
                params[SEARCH_IMG_QUERY_PARAMS.DATE] = `${
                    params[SEARCH_IMG_QUERY_PARAMS.DATE]
                }|${formattedDate}`;
            }
        }

        return params;
    };

    params = _prepareSearchReportNoAIParams(params);

    delete params[SEARCH_IMG_QUERY_PARAMS.FILTERS];
    delete params[SEARCH_IMG_QUERY_PARAMS.DATE_TO];
    delete params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY];

    url = _createURLQuery(params, url);

    /** @type {Response}*/
    const response = await fetchMethod(url, {
        method: 'GET',
        headers: _buildHeaders(),
    });

    return response;
};

/**
 * Search reports in the server. Get the details of a report.
 * @type {function}
 * @param {object} params - The params to search.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const getReportDetails = async (params) => {
    /** @type {URLSearchParams} */
    let urlParams = new URLSearchParams(window.location.search);
    urlParams = cleanUrlParams(urlParams);

    let /** @type {Function} */ fetchMethod = _abortAndFetch;

    if (
        params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY] ||
        params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS]
    )
        fetchMethod = fetch;

    /** @type {string} */
    const reportId = urlParams.has(SEARCHER_TYPES.REPORT_ID)
        ? urlParams.get(SEARCHER_TYPES.REPORT_ID)
        : params[SEARCH_IMG_QUERY_PARAMS.REPORT_ID];

    /** @type {string} */
    let url = `${ENDPOINT_GET_REPORT_DETAILS}/${reportId}`;

    if (!params[SEARCH_IMG_QUERY_PARAMS.AI]) {
        params = _adaptParamsToNoAIServer(params);
    }

    delete params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY];
    url = _createURLQuery(params, url);

    /** @type {Response} */
    const response = await fetchMethod(url, {
        method: 'GET',
        headers: _buildHeaders(),
    });

    return response;
};

/**
 * Get the fixed widgets from the server.
 * @returns {Promise<{ title: string }>} The response from the server in json format
 */
export const getFixedWidgets = async () => {
    /** @type {URLSearchParams} */
    const urlParams = new URLSearchParams(window.location.search);
    /** @type {string} */
    let url = HOME_FIXED_WIDGETS.replace(
        SEARCH_RESULT_COLLECTION_KEY,
        getCollectionForNoAI(urlParams).toLocaleLowerCase(),
    );

    /** @type {Response} */
    const response = await fetch(url, {
        method: 'GET',
        headers: _buildHeaders(),
    });

    /** @type {object} */
    const responseJson = await response.json();

    return {
        widgets: responseJson,
    };
};

/**
 * Get the lightbox collections from the server.
 * @param {object} params - The params to search.
 * @returns {Promise<Response>} - The response from the server in json format
 * @throws {Error} - If the response is not ok
 */
export const getLightBoxCollections = async (params) => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    let /** @type {Function} */ fetchMethod = _abortAndFetch;

    if (
        params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY] ||
        params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS]
    )
        fetchMethod = fetch;

    /** @type {string} */
    let url = `${ENDPOINT_LIGHTBOX_LIST}`;
    params = _adaptParamsToNoAIServer(params);
    /* For Lightbox query we need the param 'search' instead of 'txt' */
    params[SEARCH_IMG_QUERY_PARAMS.SEARCH_TEXT] = params[SEARCH_IMG_QUERY_PARAMS.TEXT];
    delete params[SEARCH_IMG_QUERY_PARAMS.TEXT];
    delete params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY];

    url = _createURLQuery(params, url);

    /** @type {Response} */
    const response = await fetchMethod(url, {
        method: 'GET',
        headers: _buildHeaders(),
    });

    return response;
};

/**
 * Get the most wanted from the server.
 * @returns {Promise<object>} - The response from the server in json format
 */
export const getMostWanted = async () => {
    /** @type {Response} */
    const response = await fetch(MOST_WANTED_URL);
    if (response.ok) {
        return response.json();
    } else {
        delayTranslate(() => {
            showNotification(
                t('notification.errors.mostWantedError'),
                NOTIFICATIONS_TYPE.ERROR,
            );
        });
    }
};

/**
 * Remove an item from the lightbox.
 * @param {string} collectionId - The collection id.
 * @returns {Promise<object>} - The response from the server in json format
 */
export const deleteLightboxCollection = async (collectionId) => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    const response = await fetch(`${ENDPOINT_LIGHTBOX_LIST}/${collectionId}`, {
        method: 'DELETE',
        headers: _buildHeaders(),
    });

    return response;
};

/**
 * Update the title of a lightbox collection.
 * @param {string} collectionId - The collection id.
 * @param {string} titleChart - The new title.
 * @returns {Promise<object>} - The response from the server in json format
 */
export const updateTitleLightboxCollection = async (collectionId, titleChart) => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    const response = await fetch(ENDPOINT_LIGHTBOX_LIST, {
        method: 'POST',
        headers: _buildHeaders(),
        body: JSON.stringify({ id: collectionId, titleChart }),
    });

    return response;
};

/**
 * Get the lightbox details.
 * @param {object} params - The params to search.
 * @returns {Promise<object>} - The response from the server in json format
 */
export const getLightboxDetails = async (params) => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );
    let /** @type {Function} */ fetchMethod = _abortAndFetch;

    if (
        params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY] ||
        params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS]
    )
        fetchMethod = fetch;

    delete params[SEARCH_IMG_QUERY_PARAMS.AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY];

    const /** @type {string} */ collectionId = urlParams.get(SEARCHER_TYPES.LIGHTBOX_ID);

    let /** @type {string} */ url = `${ENDPOINT_LIGHTBOX_LIST}/${collectionId}`;
    params = _adaptParamsToNoAIServer(params);

    url = _createURLQuery(params, url);

    /** @type {Response} */
    const response = await fetchMethod(url, {
        method: 'GET',
        headers: _buildHeaders(),
    });

    return response;
};

/**
 * Search similar images in the server.
 * @param {object} params - The params to search.
 * @returns {Promise<Response>} - The response from the server in json format
 * @throws {Error} - If the response is not ok
 */
export const searchSimImage = async (params) => {
    if (params[SEARCH_IMG_QUERY_PARAMS.AI_SERVER_MAINTENANCE]) {
        throw new Error('AI server is under maintenance');
    }

    /** @type {URLSearchParams} */
    let urlParams = new URLSearchParams(window.location.search);
    urlParams = cleanUrlParams(urlParams);

    if (!urlParams.has(SEARCH_IMG_QUERY_PARAMS.IMG_UID)) {
        throw new Error(
            `${BAD_IMAGE_ID_SEARCH_SIM_IMAGES_CODE} - Error get the image ID`,
        );
    }

    /** @type {function} */
    let fetchMethod = _abortAndFetch;

    /* check if the current url is ROUTES.DETAILS. For that view we need to use fetch instead of _abortAndFetch */
    if (isDetailsView || params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY]) {
        fetchMethod = fetch;
    }

    params = _getTypeEndpointSearch(urlParams, params);
    params = _adaptParamsToAIServer(params, urlParams);

    delete params[SEARCH_IMG_QUERY_PARAMS.IMG_UID];
    delete params[SEARCH_IMG_QUERY_PARAMS.PROV];
    delete params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY];
    delete params[SEARCH_IMG_QUERY_PARAMS.AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.PAGE_REPORT_AI];
    delete params[SEARCH_IMG_QUERY_PARAMS.RANGE];
    delete params[SEARCH_IMG_QUERY_PARAMS.CURRENT_TAB];
    delete params[SEARCH_IMG_QUERY_PARAMS.RECURSIVE_AI_SEARCH];
    delete params[SEARCH_IMG_QUERY_PARAMS.AI_SERVER_MAINTENANCE];

    const /** @type {object} */ body = {
            ...(urlParams.has(SEARCHER_TYPES.OPTION) && {
                collection: getCollectionForNoAI(urlParams),
            }),
            parameters: new URLSearchParams(params).toString(),
        };

    const /** @type {string} */ url = ENDPOINT_SEARCH_RESULTS_AI;

    /** @type {Response} */
    const response = await fetchMethod(url, {
        method: 'POST',
        headers: _buildHeaders(),
        body: JSON.stringify(body),
    });

    return response;
};

/**
 * Load a video with partial content support.
 * @param {function} fetchVideo Fetch function.
 * @param {string} url Video url.
 * @returns {Promise<Response>}
 * @throws {Error} If the video response is not ok.
 */
export const loadVideoWithPartialContentSupport = async (fetchVideo, url) => {
    /** @type {string} */
    const videoSrc = isLocalHost() ? url : url.replace(BASE_HOTS, '');
    /** @type {Response} */
    const response = await fetchVideo(videoSrc);

    if (response.status !== 206 && response.status !== 200) {
        throw new Error('Error loading the video', response);
    }

    return response;
};

/**
 * Get the download info from the server.
 * @param {string} img_uid - The image uid.
 * @param {boolean} isItem - If the download is an item.
 * @param {string} wesog_provider - The wesog provider.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const getDownloadInfo = async (
    img_uid,
    isItem = true,
    wesog_provider = PROV_FILTERS.GTRES,
) => {
    const /** @type {string} */ params = new URLSearchParams();
    params.append('isItem', isItem);

    if (isItem) {
        params.append('w_provider', wesog_provider);
    }

    /** @type {Response} */
    const response = await fetch(
        `${ENDPOINT_DOWNLOAD_OPTIONS}/${img_uid}?${params.toString()}`,
        {
            method: 'GET',
            headers: _buildHeaders(),
        },
    );

    return response;
};

/**
 * Download an active element.
 * @param {string} code - Code to download the active element.
 * @param {string} reference - Reference to download the active element.
 * @deprecated
 * @returns {void}
 */
export const downloadLightboxActive = (code, reference) => {
    /** @type {{ username: string, access_token: string, refresh_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string}} */
    const user = getWesogUserInfo();

    /** @type {string} */
    let url = `${ENDPOINT_LIGHTBOX_LIST}/${user.access_token}/${code}/${reference}`;

    fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
            /** @type {string} */
            const url = URL.createObjectURL(blob);
            /** @type {HTMLAnchorElement} */
            const a = document.createElement('a');

            a.href = url;
            a.download = `${reference || 'image'}`;
            a.click();
        });
};

/**
 * Get the Cart list state from the server.
 * @param {boolean} detail - If the detail is required.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const getCartState = async (detail = false) => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    const /** @type {Response} */ response = await fetch(
            `${ENDPOINT_CART_LIST}?detail=${detail}`,
            {
                method: 'GET',
                headers: _buildHeaders(),
            },
        );

    return response;
};

/**
 * Update the Cart list state from the server.
 * @param {string} idElement - The element ID.
 * @param {boolean} isItem - If the element is an item.
 * @param {boolean} add - If the element is added.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const updateCartState = async (idElement, isItem = true, add = true) => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    if (!idElement) {
        throw new Error(`${BAD_IMAGE_ID_SEARCH_SIM_IMAGES_CODE} - Bad element ID`);
    }

    if (!isItem && !add) {
        throw new Error(`${BAD_FILTERS_SEARCH_IMAGES_CODE} - Bad params to update cart`);
    }

    const /** @type {string} */ url = `${ENDPOINT_CART_LIST}/${idElement}?isItem=${isItem}&add=${add}`;

    const /** @type {Response} */ response = await fetch(url, {
            method: 'GET',
            headers: _buildHeaders(),
        });

    return response;
};

/**
 * Clean the Cart list state from the server.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const cleanCartState = async () => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    const /** @type {Response} */ response = await fetch(ENDPOINT_CART_LIST, {
            method: 'DELETE',
            headers: _buildHeaders(),
        });

    return response;
};

/**
 * Download the Cart list from the server.
 * @param {object[]} cartOptions - The cart options.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const downloadCart = async (cartOptions) => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    const /** @type {Response} */ response = await _abortAndFetch(
            `${ENDPOINT_DOWNLOAD_OPTIONS}?ischart=true`,
            {
                method: 'POST',
                headers: _buildHeaders(),
                body: JSON.stringify(cartOptions),
            },
        );

    return response;
};

/**
 * Update the lightbox state from the server.
 * @param {string} idElement - The element ID.
 * @param {string} idLightbox - The lightbox ID.
 * @param {boolean} add - If the element is added.
 * @param {number} element - The element type.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const updateLightboxState = async (
    idElement,
    idLightbox,
    add = true,
    element = ELEMENT_TYPE_ADD_LIGHTBOX.ITEM,
) => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    const /** @type {string} */ url = `${ENDPOINT_LIGHTBOX_LIST}/${idLightbox}/${idElement}?add=${add}&element=${element}`;

    const /** @type {Response} */ response = await fetch(url, {
            method: 'GET',
            headers: _buildHeaders(),
        });

    return response;
};

/**
 * Create a new lightbox in the server.
 * @param {string} titleChart - The title of the lightbox.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const createNewLightbox = async (titleChart) => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    if (!titleChart) {
        throw new Error(`${BAD_FILTERS_SEARCH_IMAGES_CODE} - Bad title`);
    }

    const /** @type {Response} */ response = await fetch(ENDPOINT_LIGHTBOX_LIST, {
            method: 'POST',
            headers: _buildHeaders(),
            body: JSON.stringify({ titleChart }),
        });

    return response;
};

/**
 * Update the default item of a lightbox in the server.
 * @param {string} idElement - The element ID.
 * @param {string} idLightbox - The lightbox ID.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const updateDefaultItemLightbox = async (idElement, idLightbox) => {
    if (!isUserLoggedIn()) {
        throw new Error(`${USER_NOT_FOUND_CODE} - User not found`);
    }

    if (!idElement || !idLightbox) {
        throw new Error(`${BAD_FILTERS_SEARCH_IMAGES_CODE} - Bad params`);
    }

    const /** @type {Response} */ response = await fetch(ENDPOINT_LIGHTBOX_LIST, {
            method: 'POST',
            headers: _buildHeaders(),
            body: JSON.stringify({
                id: idLightbox,
                defaultItem: idElement,
            }),
        });

    return response;
};

/**
 * Get a temporal report details.
 * @param {object} params - The params to search.
 * @returns {Promise<object>} - The response from the server in json format
 */
export const getTemporalReportDetails = async (params) => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );
    let /** @type {Function} */ fetchMethod = _abortAndFetch;

    if (
        params[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY] ||
        params[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS]
    )
        fetchMethod = fetch;

    const /** @type {string} */ collectionId = urlParams.get(SEARCHER_TYPES.REPORT_ID);
    const /** @type {string} */ url = `${ENDPOINT_REPORTS_LIST}?idfeature=${collectionId}`;

    /** @type {Response} */
    const response = await fetchMethod(url, {
        method: 'GET',
        headers: _buildHeaders(),
    });

    return response;
};

/**
 * Get the character list from the server.
 * @returns {Promise<Response>} - The response from the server in json format
 */
export const getCharactersList = async () => {
    const response = await fetch(ENDPOINT_DOWNLOAD_OPTIONS, {
        method: 'GET',
        headers: _buildHeaders(),
    });

    return response;
};
